import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { Button } from './Button';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface DialogProps {
  title: string;
  description?: string;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * Diálogo modal acessível (RNF-EU02).
 *
 * Três responsabilidades que, se ficarem a cargo de quem usa o componente,
 * alguém vai esquecer: prender o foco enquanto aberto, fechar no `Esc` e
 * devolver o foco a quem abriu. Como este é o primeiro modal reusável da SPA,
 * errar aqui erraria em todo lugar que o reusar depois.
 */
export function Dialog({ title, description, onClose, footer, children }: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = description ? `${titleId}-desc` : undefined;

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // O foco vai para o painel, não para o primeiro controle: assim o leitor de
    // tela anuncia o título antes de anunciar um botão solto.
    panelRef.current?.focus();

    const onKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus();
    };
  }, [onClose]);

  /** Tab circula dentro do painel: sem isso o teclado escapa para a página atrás. */
  const trapFocus = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'Tab' || !panelRef.current) return;

    const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) return;

    const first = items[0]!;
    const last = items[items.length - 1]!;
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === panelRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center bg-overlay p-4">
      {/* Clicar fora fecha. Fica escondido da árvore de acessibilidade e fora
          da ordem de tabulação: é atalho de mouse, e expô-lo criaria um segundo
          controle chamado "Fechar" competindo com o do cabeçalho. Quem usa
          teclado tem o Esc e o botão. */}
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        onKeyDown={trapFocus}
        className="relative z-10 flex max-h-full w-full max-w-xl flex-col gap-4 overflow-y-auto rounded-lg border border-border bg-surface p-5 shadow-md focus:outline-none"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id={titleId} className="text-lg font-semibold text-text">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="mt-1 text-sm text-text-muted">
                {description}
              </p>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Fechar
          </Button>
        </div>

        <div className="flex flex-col gap-4">{children}</div>

        {footer && <div className="flex flex-wrap justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}
